/**
 * O número de mensagens não lidas do chat para o menu lateral — D2 (passo 14, 07/10/2026), desenho §6.1.
 *
 * Mora no Sidebar, que existe em toda página: o número tem de estar vivo mesmo com o chat fechado.
 * Por isso lê a MESMA query da tela do chat (`opcoesDaListaDeSalas()`: mesma chave
 * `[...ROOMS_KEY, undefined]`, mesma função, mesmo `staleTime`). Com o chat aberto não custa
 * requisição nenhuma; fora dele é 1 `GET /chat/rooms` e, daí em diante, o refetch que o
 * `chat:mensagem_nova` e o `chat:rooms_invalidate` já provocam (a query está sempre ativa com o
 * Sidebar montado — medido em 07/10: 180 mensagens em 14 dias, aceito).
 *
 * A regra do número está em `contarNaoLidasDoChat` (pura, com teste próprio):
 *   - conta `DM`, `GROUP` e `SECTOR_AUTO` não arquivadas (as da aba "Interno");
 *   - INCLUI as silenciadas: silenciar tira o aviso e o som, não o número ("conta, mas não grita");
 *   - EXCLUI `ENTITY_COMMENTS` (comentários do Drive) e `VOICE_CHANNEL`, que têm o próprio sino;
 *   - EXCLUI a conversa que a pessoa está olhando (sala aberta com a aba visível, de `salaAberta.ts`).
 *     A mesma regra da lista de conversas (`salasParaExibir.ts`): sem ela o número piscaria entre
 *     o `GET` que ainda traz o `unread_count` velho e a gravação da leitura no Postgres (~1 s).
 */
import { useMemo, useSyncExternalStore } from "react";
import { useQuery } from "@tanstack/react-query";

import { assinarSalaAberta, salaAbertaVisivel } from "@/lib/avisos/salaAberta";
import type { ChatRoom, ChatRoomType } from "@/types/chat";
import { opcoesDaListaDeSalas } from "./useChat";

export interface NaoLidasDoChat {
  /** Soma das não lidas das conversas que contam. */
  total: number;
  /** Alguma dessas conversas tem menção a você (o selo fica vermelho). */
  temMencao: boolean;
}

/** As conversas do chat "de verdade". Comentário do Drive e canal de voz têm o próprio sino. */
const SALAS_QUE_CONTAM: ReadonlySet<ChatRoomType> = new Set<ChatRoomType>([
  "DM",
  "GROUP",
  "SECTOR_AUTO",
]);

/**
 * Sempre o mesmo objeto no caso zerado: a identidade estável serve a quem põe o resultado em
 * dependências de hook. ⚠️ Não poupa o re-render de quem chama o hook (o Sidebar): o react-query
 * rastreia `data`, que muda a cada alteração da lista de salas, mesmo com o número igual.
 */
const ZERADO: NaoLidasDoChat = Object.freeze({ total: 0, temMencao: false });

/**
 * Pura: a mesma lista e a mesma sala à vista dão o mesmo número. `salaAVista` é o id da conversa
 * aberta com a aba visível (ou `null`).
 */
export function contarNaoLidasDoChat(
  rooms: readonly ChatRoom[] | undefined,
  salaAVista: string | null
): NaoLidasDoChat {
  if (!rooms) return ZERADO;
  let total = 0;
  let temMencao = false;
  for (const sala of rooms) {
    if (sala.archived || !SALAS_QUE_CONTAM.has(sala.room_type) || sala.id === salaAVista) continue;
    const naoLidas = sala.unread_count ?? 0;
    total += naoLidas;
    if (sala.has_unread_mentions) temMencao = true;
  }
  return total === 0 && !temMencao ? ZERADO : { total, temMencao };
}

/**
 * `ativo` = a pessoa pode ler o chat (`chat:read`): sem a permissão não há busca e o número é zero.
 * Chame UMA vez, no Sidebar.
 */
export function useNaoLidasDoChat({ ativo }: { ativo: boolean }): NaoLidasDoChat {
  const { data } = useQuery({ ...opcoesDaListaDeSalas(), enabled: ativo });
  // `string | null`: o snapshot é comparável por valor, então o React não re-renderiza sem mudança.
  const salaAVista = useSyncExternalStore(assinarSalaAberta, salaAbertaVisivel, () => null);

  return useMemo(
    () => (ativo ? contarNaoLidasDoChat(data, salaAVista) : ZERADO),
    [ativo, data, salaAVista]
  );
}
